import makeStyles from '@mui/styles/makeStyles';
import { brandclub_colors } from '@stackline/ui';
import React, { useEffect, useMemo } from 'react';
import { TopicExportModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicExports/TopicExportModal';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { AppLinkTabs } from 'src/components/GlobalComponents/PrimaryPageNavigation/AppLinkTabs';
import { PRIMARY_TAB_INDICES } from 'src/components/GlobalComponents/PrimaryPageNavigation/constants';
import { useUpdateTimePeriods } from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import styles from 'src/components/BeaconV2Components/styles/styles';
import { TabRoute } from 'src/components/CommonV2Components/GenericSideBar/types';
import { calculateNewQueryParams } from 'src/store/modules/app/selectors';
import { onContentGenerationMode, onShortagesMode } from 'src/store/modules/user/selectors';
import { EntityPathname } from 'src/types/application/types';
import { useAppSelector, useHistory } from 'src/utils/Hooks';
import {
  getQueryParamValue,
  shouldShowBeaconAdvertising,
  getAppSubscriptionTier,
  isBeaconCriteo,
  shouldShowDigitalShelfTab
} from 'src/utils/app';
import colors from 'src/utils/colors';

/**
 * Helper function to build generation routes that append '/generate' to client paths
 */
const buildGenerationRoute = (clientId: string, searchParams: string, tab: string, subtab: string) => {
  return `/client/${clientId}/generate${searchParams}&tab=${tab}&subtab=${subtab}`;
};

const useStyles = makeStyles({
  navbar: {
    backgroundColor: brandclub_colors.white,
    boxShadow: '0 1px 4px 0 rgba(21, 34, 50, 0.08)',
    position: 'initial',
    top: 60,
    zIndex: 1000
  },
  tabsContainer: {
    paddingTop: '8px',
    paddingBottom: '8px',
    WebkitMaskImage: 'none'
  },
  tabs: {
    ...styles.pageCenterDiv,
    width: styles.pageCenterDiv.width,
    margin: `0 auto`,
    '& div.MuiTabs-flexContainer': {
      alignItems: 'center',
      paddingLeft: 0,
      overflowX: 'hidden',
      gap: '18px'
    }
  },
  tab: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Roboto',
    color: colors.darkBlue,
    padding: 0,
    textTransform: 'none',
    '&.MuiButtonBase-root': {
      minHeight: 26,
      minWidth: 'unset',
      textDecoration: 'none'
    },
    '&.Mui-selected': {
      borderRadius: 13,
      padding: '4px 16px',
      backgroundColor: colors.darkBlue,
      color: brandclub_colors.white
    }
  }
});

interface PrimaryNavBarProps {
  location: any;
}

export const PrimaryNavBar = ({ location }: PrimaryNavBarProps) => {
  const history = useHistory();
  const app = useAppSelector((state) => state.app);
  const user = useAppSelector((state) => state.user);
  const retailer = useAppSelector((state) => state.retailer);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const updateTimePeriods = useUpdateTimePeriods({ history });
  const entityType = getQueryParamValue('entityType');
  const action = getQueryParamValue('action');
  const entityIdBeingEdited = getQueryParamValue('id');
  const searchPage = !!getQueryParamValue('searchPage');
  // Note: Beacons "Home" is actually the "client" route. Categories, Brands, Segments, etc are under "Home" currently.
  const subscriptionTier = getAppSubscriptionTier();
  const isBasic = subscriptionTier === 'basic';

  const timePeriods = mainTimePeriod.availableMainTimePeriods;
  const handleDateRangeChange = (periodId: string) => {
    const newTimePeriod = timePeriods.find((period) => period.id === periodId);
    updateTimePeriods(newTimePeriod);
  };

  const tabRoutes: TabRoute[] = [
    {
      label: 'Home',
      route: `/client/${user.config.vendor.hashId}${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=${app.defaultQueryParams.tab}&subtab=${app.defaultQueryParams.subtab}`,
      index: PRIMARY_TAB_INDICES.Home
    },
    {
      label: 'Categories',
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=category`,
      index: PRIMARY_TAB_INDICES.Categories
    },
    {
      label: 'Segments',
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=segment`,

      index: PRIMARY_TAB_INDICES.Segments
    },
    {
      label: 'Topics',
      requireSubscription: isBasic,
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=searchtermlist`,
      index: PRIMARY_TAB_INDICES.Topics
    },
    {
      label: 'Brands',
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=brand`,
      index: PRIMARY_TAB_INDICES.Brands
    }
  ];

  if (shouldShowDigitalShelfTab()) {
    tabRoutes.push({
      label: 'Shelf',
      route: `/home${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=summary&subtab=${app.name}&entityType=shelf`,
      index: PRIMARY_TAB_INDICES.Shelf
    });
  }

  if (shouldShowBeaconAdvertising()) {
    tabRoutes.push({
      label: 'Advertising',
      route: `/client/${user.config.vendor.hashId}${
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams
      }&tab=${BEACON_TABS.ADVERTISING}&subtab=${BEACON_SUBTABS.AD_METRICS}`,
      index: PRIMARY_TAB_INDICES.Advertising,
      disabled: isBeaconCriteo()
    });
  }

  if (onContentGenerationMode()) {
    tabRoutes.push({
      label: 'Content',
      route: buildGenerationRoute(
        user.config.vendor.hashId,
        calculateNewQueryParams(
          app,
          { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
          mainTimePeriod,
          comparisonTimePeriod
        ).searchParams,
        BEACON_TABS.PROFILES,
        BEACON_SUBTABS.PROFILES_ACTIVE
      ),
      index: PRIMARY_TAB_INDICES.Generation
    });
  }

  tabRoutes.push({
    label: 'Finance',
    route: `/client/${user.config.vendor.hashId}${
      calculateNewQueryParams(
        app,
        { id: retailer.id === '0' ? Object.keys(allWeekIdsByRetailerId)[1] : retailer.id },
        mainTimePeriod,
        comparisonTimePeriod
      ).searchParams
    }&tab=${BEACON_TABS.OPERATIONS}&subtab=${BEACON_SUBTABS.DISPUTES}`,
    index: PRIMARY_TAB_INDICES.Finance,
    disabled: !onShortagesMode() // TODO: If we split Dispute Management and Shortages subscriptions, we will need to replace this
  });

  const activeTabIndex = useMemo(() => {
    if (location.search.includes('tab=advertising')) {
      return PRIMARY_TAB_INDICES.Advertising;
    }
    if (location.pathname.includes('/generate')) {
      return PRIMARY_TAB_INDICES.Generation;
    }
    if (location.pathname === EntityPathname.Search) {
      if (searchPage) {
        return undefined;
      }
      if (entityType === 'segment') {
        return PRIMARY_TAB_INDICES.Segments;
      } else if (entityType === 'searchtermlist') {
        return PRIMARY_TAB_INDICES.Topics;
      } else {
        return PRIMARY_TAB_INDICES.Home;
      }
    }
    if (location.pathname.includes(EntityPathname.Client)) {
      if (location.search.includes('subtab=disputes')) {
        return PRIMARY_TAB_INDICES.Finance;
      }
      return PRIMARY_TAB_INDICES.Home;
    }

    if (location.pathname === EntityPathname.Home) {
      switch (entityType) {
        case 'category':
          return PRIMARY_TAB_INDICES.Categories;
        case 'segment':
          return PRIMARY_TAB_INDICES.Segments;
        case 'searchtermlist':
          return PRIMARY_TAB_INDICES.Topics;
        case 'brand':
          return PRIMARY_TAB_INDICES.Brands;
        case 'shelf':
          return PRIMARY_TAB_INDICES.Shelf;
        default:
          return PRIMARY_TAB_INDICES.Home;
      }
    }

    // If we can't find the active tab, default to the first tab (home)
    return PRIMARY_TAB_INDICES.Home;
  }, [location.pathname, location.search, entityType, entityIdBeingEdited, action]);

  const classes = useStyles();

  const [, setSelectedTabIndex] = React.useState<number>(activeTabIndex);

  const resetAdvertisingTimePeriods = () => {
    handleDateRangeChange('mtd');
  };
  const resetNonAdvertisingTimePeriods = () => {
    handleDateRangeChange('ytd');
  };

  useEffect(() => {
    setSelectedTabIndex((preIndex) => {
      if (activeTabIndex !== PRIMARY_TAB_INDICES.Advertising && preIndex === PRIMARY_TAB_INDICES.Advertising) {
        resetNonAdvertisingTimePeriods();
      } else if (activeTabIndex === PRIMARY_TAB_INDICES.Advertising && preIndex !== PRIMARY_TAB_INDICES.Advertising) {
        resetAdvertisingTimePeriods();
      }
      return activeTabIndex;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex]);

  return (
    <div className={classes.navbar}>
      <div className={classes.tabsContainer}>
        <TopicExportModal />

        <AppLinkTabs
          tabIndex={activeTabIndex}
          setTabIndex={(_) => {}}
          tabRoutes={tabRoutes}
          tabsProps={{
            classes: {
              root: classes.tabs
            }
          }}
          tabProps={{
            classes: {
              root: classes.tab
            }
          }}
        />
      </div>
    </div>
  );
};
